import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import store from '@state/store'
import { formatQueryParams } from '@utils/Format'
import { transcricoes as endpoint } from '@modules/crm/constants/URL'

// Sem cache via store: todas as leituras aqui são listagens filtradas/parametrizadas
// (q, status, ai_status, datas, paginação) ou detalhes pontuais por id, então não há
// um conjunto estável de dados para guardar na memória do Vuex.
class APICrmTranscricoes extends APITemplate {
  async listar({
    q,
    status,
    ai_status,
    id_cliente,
    id_profissional,
    data_inicio,
    data_fim,
    tem_sugestoes,
    limit,
    offset,
  } = {}) {
    const url =
      endpoint.list +
      formatQueryParams({
        q,
        status,
        ai_status,
        id_cliente,
        id_profissional,
        data_inicio,
        data_fim,
        tem_sugestoes,
        limit,
        offset,
      })
    return (
      (await con.get(url)) ||
      this.buildErrorResponse({ message: 'Erro inesperado' })
    )
  }

  // Contagem de reuniões acontecendo agora (card "Ao vivo" do Histórico). Não dá
  // para derivar dos itens já carregados: a listagem é paginada e uma reunião em
  // andamento pode estar fora do lote. `limit: 1` porque só o `total` interessa —
  // é o menor payload que o backend aceita para responder a contagem.
  // Devolve número (nunca resposta), e 0 em qualquer falha: o card é acessório e
  // não pode impedir a listagem de renderizar.
  async contarAoVivo() {
    const r = await this.listar({ status: 'in_progress', limit: 1 })
    if (r && r.status === 200 && r.value) return r.value.total || 0
    return 0
  }

  async detalhar({ id } = {}) {
    return (
      (await con.get(endpoint.detail(id))) ||
      this.buildErrorResponse({ message: 'Erro inesperado' })
    )
  }

  async regenerarSummary({ id } = {}) {
    return (
      (await con.post(endpoint.regenerate(id), {})) ||
      this.buildErrorResponse({ message: 'Erro inesperado' })
    )
  }

  // Edição manual: cliente vinculado, projeto vinculado, participantes, nome
  // (title) e/ou descrição da reunião (update parcial — só envia os campos
  // presentes). id_cliente/id_project aceitam null para desvincular.
  async atualizar({
    id,
    id_cliente,
    id_project,
    participants,
    title,
    description,
  } = {}) {
    const body = {}
    if (id_cliente !== undefined) body.id_cliente = id_cliente
    if (id_project !== undefined) body.id_project = id_project
    if (participants !== undefined) body.participants = participants
    if (title !== undefined) body.title = title
    if (description !== undefined) body.description = description
    return (
      (await con.post(endpoint.update(id), body)) ||
      this.buildErrorResponse({ message: 'Erro inesperado' })
    )
  }

  // Lista de clientes acessíveis (para o seletor de vínculo).
  async listarClientes() {
    return (
      (await con.get(endpoint.clientsList)) ||
      this.buildErrorResponse({ message: 'Erro inesperado' })
    )
  }

  // Scores por critério de uma transcrição (aba Avaliação) — reuso do read da
  // spec-001. value = [TranscriptionScore] (name_criteria, score, score_reason[],
  // improvement_suggestion[]); vazio se a transcrição não tem projeto/score.
  async listarScores({ id } = {}) {
    return (
      (await con.get(endpoint.scores(id))) ||
      this.buildErrorResponse({ message: 'Erro inesperado' })
    )
  }

  // Carrega os scores de uma transcrição e os indexa no store por id (helper de
  // indexação na API — modelo apiCrmCalendar). O modal chama este método direto e
  // lê pelo getter transcriptScores(id); não há action intermediária. Dedup por id
  // via getter de loading. Não-fatal: erro deixa a lista vazia (a aba Avaliação cai
  // no estado vazio) sem quebrar o detalhe.
  async carregarScores({ id } = {}) {
    if (!id) return null
    if (store.getters['crm/transcriptScoresLoading'](id)) return null

    store.dispatch('crm/setTranscriptScoresLoading', { id, loading: true })
    try {
      const r = await this.listarScores({ id })
      const scores =
        r && r.status === 200 && Array.isArray(r.value) ? r.value : []
      store.dispatch('crm/setTranscriptScores', { id, scores })
      return r
    } finally {
      store.dispatch('crm/setTranscriptScoresLoading', { id, loading: false })
    }
  }
}

const apiCrmTranscricoes = new APICrmTranscricoes()
export { apiCrmTranscricoes }
